import { createContext, PropsWithChildren, useContext } from "react";
import { CaptchaConfigProvider } from "../../utils/captcha/captcha-config-context.tsx";

export interface LoginConfigContextStore {
  captchaSiteKey: string;
  facebookAppId: string;
  googleClientId: string;
  appleClientId: string;
  appleRedirectUri: string;
}

export const LoginConfigContext = createContext<LoginConfigContextStore | undefined>(undefined);

export const useLoginConfig = () => useContext(LoginConfigContext);

/*
 * Provides the login configuration to the components that need it.
 * Expects server configuration in 'globalThis.serverConfig'
 * If a context was already provided it's a noop
 *
 * Note: This provider wraps children with CaptchaConfigProvider for backward compatibility.
 * Components that only need captcha config can use CaptchaConfigProvider directly.
 */
export const LoginConfigProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const context = useContext(LoginConfigContext);
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/globalThis
  if (globalThis.serverConfig === undefined && context === undefined) {
    console.warn("LoginConfigProvider, no config is found. Context is not set. Noop");
  }
  if (globalThis.serverConfig !== undefined) {
    const serverConfig = globalThis.serverConfig as LoginConfigContextStore;
    const config: LoginConfigContextStore = {
      captchaSiteKey: serverConfig.captchaSiteKey,
      googleClientId: serverConfig.googleClientId,
      facebookAppId: serverConfig.facebookAppId,
      appleClientId: serverConfig.appleClientId,
      appleRedirectUri: serverConfig.appleRedirectUri,
    };

    return (
      <CaptchaConfigProvider>
        <LoginConfigContext.Provider value={config}>{children}</LoginConfigContext.Provider>
      </CaptchaConfigProvider>
    );
  } else {
    return <>{children}</>;
  }
};
